import type { WebGLUnavailableReason } from '../types';
import { hasCreatedWebGL2Context } from '../../monitoring/webgl-context-registry';

/**
 * Why a map that failed to start couldn't get WebGL2, or `undefined` if it failed for
 * some other reason.
 *
 * Esri's `webgl:required` only means it couldn't get a WebGL2 context at the moment it
 * checked. If this page already had one, the machine supports WebGL2 and something has
 * blocked it since. In production that follows context losses earlier in the session,
 * often several, which fits Chromium turning off GPU acceleration after repeated GPU
 * process crashes. Those users used to be told their machine lacked WebGL2 and to
 * contact IT.
 *
 * Only this page's history counts: after a reload the registry starts empty, so a
 * blocked machine reloading into the failure still gets the "not supported" message.
 */
export function getWebGLUnavailableReason(error: unknown): WebGLUnavailableReason | undefined {
    if ((error as { name?: unknown } | null)?.name !== 'webgl:required') {
        return undefined;
    }
    return hasCreatedWebGL2Context() ? 'stopped-working' : 'not-supported';
}
